import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import { useMutation } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { getCameraActions, PageLayout } from 'components/PageLayout';
import { getDeviceInfo } from 'state/localStorageTyped';
import { RecurlyBillingInformation } from 'features/payments/components/RecurlyBillingInformation';
import { useDonationState } from 'features/payments/hooks/useDonationState';
import {
  ProcessDonationPaymentTokenData,
  ProcessInitialDonationPaymentTokenData,
  SavePaymentMethodData,
} from 'shared/types';
import { API } from 'api';
import { usePaymentApi } from 'hooks/usePaymentApi';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { ENV } from 'shared/ENV';
import { usePurchaseTracking } from 'hooks/usePurchaseTracking';

const Content = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    email,
    firstName,
    lastName,
    clientToken,
    optInToUpdates,
    donationAmount,
    donationRenews,
    verificationToken,
    userId,
    updatePaymentMethod,
  } = useDonationState();
  const history = useHistory();
  const { logPurchaseEvent } = usePurchaseTracking();

  const { processDonationPaymentMutation } = usePaymentApi<
    ProcessInitialDonationPaymentTokenData | ProcessDonationPaymentTokenData
  >({
    onSuccess: () => {
      // Log purchase event for credit card donation
      logPurchaseEvent({
        productId: donationRenews ? 'donation_recurring' : 'donation_onetime',
        price: donationAmount,
        transactionType: 'donation',
        paymentMethod: 'credit_card',
        isUpgrade: false,
      });
      history.push('/donate/success');
    },
  });

  const savePaymentMethodMutation = useMutation<
    void,
    Error,
    SavePaymentMethodData,
    unknown
  >({
    mutationFn: async (data) => {
      await API.post('recurly_integration/update_billing_info/', data);
    },
    onSuccess: () => {
      history.push('/donate/account_info');
    },
  });

  return (
    <>
      <Helmet>
        <title>{t('donationBillingInformation.title')}</title>
      </Helmet>
      <SimplePageLayout>
        <RecurlyProvider publicKey={ENV.VITE_RECURLY_PUBLIC_TOKEN}>
          <Elements>
            <RecurlyBillingInformation<
              | ProcessInitialDonationPaymentTokenData
              | ProcessDonationPaymentTokenData
            >
              transactionType="donation"
              initialState={{
                email,
                firstName,
                lastName,
                clientToken,
                optInToUpdates,
                transactionAmount: donationAmount,
                donationRenews,
                verificationToken,
                userId,
                updatePaymentMethod,
              }}
              processPaymentMutation={processDonationPaymentMutation}
              savePaymentMethodMutation={savePaymentMethodMutation}
              checkoutConfig={{
                title: t('donate.total'),
                description: t('donate.footer'),
              }}
            />
          </Elements>
        </RecurlyProvider>
      </SimplePageLayout>
    </>
  );
};

export const DonateBillingInformation = (): JSX.Element => {
  const { t } = useTranslation();
  const deviceInfo = getDeviceInfo();
  const isMobilePlatform = Boolean(deviceInfo?.isMobilePlatform);

  return (
    <PageLayout
      data-cname="DonationBillingInformationPage"
      allowAppBanner={false}
      headerBarProps={{
        action: isMobilePlatform ? 'noControls' : 'back',
        iconActions: getCameraActions(t),
      }}
    >
      <Content />
    </PageLayout>
  );
};
